import { useEffect, useState } from "react";
import { ChevronLeft, Loader2, ChevronDown, ChevronUp, Check, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import type { AdminOrderListItem, OrderDetailsResponse, ReplacementEntry } from "./types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import OrderDetailsHeader from "./OrderDetailsHeader";
import PrescriptionList from "./PrescriptionList";
import PatientOrderSidebar from "./PatientOrderSidebar";
import ApproveOrderTable from "./ApproveOrderTable";
import RejectOrderSection from "./RejectOrderSection";
import { firestore as db } from "../../firebase/firebase";
import { collection, doc, setDoc } from "firebase/firestore";
import CancelOrderModal from "./CancelOrderModal";

interface ApproveOrderProps {
  order: AdminOrderListItem;
  onBack: () => void;
}

const ApproveOrder: React.FC<ApproveOrderProps> = ({ order, onBack }) => {
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showPrescriptions, setShowPrescriptions] = useState(false);
  const [showDoctorInfo, setShowDoctorInfo] = useState(false);
  const [replacements, setReplacements] = useState<Record<string, ReplacementEntry>>({});
  const [actionState, setActionState] = useState<"none" | "rejecting" | "approving">("none");
  const [rejectingApiCall, setRejectingApiCall] = useState(false);
  const [initialClinicStock, setInitialClinicStock] = useState<any[]>([]);
  const [doctorName, setDoctorName] = useState("");
  const [doctorRegistrationNumber, setDoctorRegistrationNumber] = useState("");

  useEffect(() => {
    fetchOrderDetails();
  }, [order.orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await AdminOrderManagementService.getOrderDetails(order.orderId);
      setOrderDetails(response);

      // Parallelly fetch clinic stock once order details is retrieved to get clinicId
      if (response && response.clinicId) {
        AdminOrderManagementService.getClinicStock(response.clinicId, 1, 15, "")
          .then((stockRes) => {
            if (stockRes.success && stockRes.data) {
              const data = stockRes.data as any;
              setInitialClinicStock(data.stocks || data.data || []);
            }
          })
          .catch((err) => console.error("Parallel stock fetch error:", err));
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  const handleReplace = (productId: string, entry: ReplacementEntry) => {
    setReplacements((prev) => ({
      ...prev,
      [productId]: entry,
    }));
  };

  const handleRevert = (productId: string) => {
    setReplacements((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const handleReject = async (reason: string) => {
    try {
      setRejectingApiCall(true);
      const res = await AdminOrderManagementService.cancelOrder({
        orderId: order.orderId,
        cancelledBy: "ADMIN",
        cancelationReason: reason,
      });
      if (res.success) {
        toast.success("Order rejected successfully!");
        onBack();
      } else {
        toast.error(res.message || "Failed to reject order");
      }
    } catch (error) {
      toast.error("Failed to reject order");
    } finally {
      setRejectingApiCall(false);
    }
  };

  const addNotificationInFirebase = async () => {
    if (!orderDetails) return;
    const docRef = doc(
      collection(db, "paymentCompletion"),
      String(orderDetails.orderId)
    );
    await setDoc(
      docRef,
      {
        message: `Some medicines are substituted by the admin for your order ${orderDetails.orderNumber}. Please approve to proceed further`,
        title: "Medicines substituted by admin",
        toId: orderDetails.patientId || "",
        status: "Unread",
        order_number: orderDetails.orderNumber || "",
        order_date: orderDetails.orderDate || "",
        isLabtestOrder: true
      },
      { merge: true }
    );
  };

  const handleApprove = async () => {
    if (!orderDetails) return;
    try {
      setActionState("approving");
      const productsPayload = orderDetails.products
        .filter((p) => !!replacements[p.productId])
        .map((p) => {
          const replacement = replacements[p.productId];
          return {
            productOrderItemId: p.productItemId,
            orderedProductId: p.productId.toString(),
            substituteProductId: replacement.substituteProductId,
            substituteInwardId: replacement.substituteInwardId,
            substitutePrice: replacement.substitutePrice,
            substituteMrp: replacement.substituteMrp,
            substituteNote: replacement.substituteNote,
          };
        });

      const resCheck = productsPayload.map(async (item) => {
        try {
          if (!item.substituteProductId) {
            return false;
          }
          const checkRes = await AdminOrderManagementService.checkProduct(item.substituteProductId);
          return checkRes.success && checkRes.doctorNameReq;
        } catch (e) {
          console.error("Check product error for ID " + item.substituteProductId, e);
          return false;
        }
      });

      const checkResults = await Promise.all(resCheck);
      const isDoctorNameRequired = checkResults.some((req) => req === true);

      if (isDoctorNameRequired && !doctorName.trim()) {
        toast.error("Doctor's name is mandatory for this order as some medicines require doctor validation.");
        setActionState("none");
        return;
      }

      const res = await AdminOrderManagementService.approveOrder({
        orderId: orderDetails.orderId,
        products: productsPayload,
        doctorName: doctorName.trim() || undefined,
        doctorRegistrationNumber: doctorRegistrationNumber.trim() || undefined,
      });

      if (res.success) {
        if (productsPayload.length > 0) {
          try {
            await addNotificationInFirebase();
          } catch (fbErr) {
            console.error("Failed to add Firebase notification:", fbErr);
          }
        }
        toast.success("Order approved successfully!");
        onBack();
      } else {
        toast.error(res.message || "Failed to approve order");
      }
    } catch (error) {
      toast.error("Failed to approve order");
    } finally {
      setActionState("none");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={32} className="animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="p-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700">
          <ChevronLeft size={16} /> Back to Orders
        </button>
        <p className="text-sm text-red-500">Failed to load order details.</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-50 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto p-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700">
          <ChevronLeft size={16} /> Back to Orders
        </button>

        <div className="flex gap-5">
          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Collapsible Order Details Header */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-semibold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowDetails(!showDetails)}
              >
                <span>Approve Order Details ({orderDetails.orderNumber})</span>
                {showDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showDetails && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <OrderDetailsHeader order={orderDetails} onUpdateSuccess={fetchOrderDetails} />
                </div>
              )}
            </div>

            {/* Prescriptions */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowPrescriptions(!showPrescriptions)}
              >
                <span className="uppercase tracking-wider">Uploaded Prescriptions</span>
                {showPrescriptions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showPrescriptions && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <PrescriptionList
                    prescriptions={orderDetails.prescriptions}
                    orderId={orderDetails.orderId}
                    orderDetails={orderDetails}
                    onUploadSuccess={fetchOrderDetails}
                  />
                </div>
              )}
            </div>

            {/* Doctor Information (Optional Fields) */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowDoctorInfo(!showDoctorInfo)}
              >
                <span className="uppercase tracking-wider">Doctor Information (Optional)</span>
                {showDoctorInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showDoctorInfo && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Doctor Name</label>
                      <input
                        type="text"
                        value={doctorName}
                        onChange={(e) => setDoctorName(e.target.value)}
                        placeholder="Enter doctor's name"
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all bg-slate-50/50"
                      />
                    </div>
                    <div>
                      <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Doctor Registration Number</label>
                      <input
                        type="text"
                        value={doctorRegistrationNumber}
                        onChange={(e) => setDoctorRegistrationNumber(e.target.value)}
                        placeholder="Enter doctor's registration number"
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all bg-slate-50/50"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Approve Order Table (Replacements) */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 z-[100]">
              <ApproveOrderTable
                products={orderDetails.products}
                clinicId={orderDetails.clinicId}
                replacements={replacements}
                onReplace={handleReplace}
                onRevert={handleRevert}
                initialClinicStock={initialClinicStock}
                isSubstitutionAllowed={orderDetails.isSubstitutionAllowed}
              />
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-[280px] flex-shrink-0 hidden lg:block">
            <PatientOrderSidebar
              products={orderDetails.products}
              replacements={replacements}
              totalAmount={orderDetails.totalAmount}
              ordernote={orderDetails.ordernote || (orderDetails as any).orderNote}
              actionButton={
                <div className="flex flex-col gap-2">
                  <button
                    className="w-full py-2.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg transition-all border-none cursor-pointer disabled:opacity-40 flex items-center justify-center gap-1.5"
                    onClick={handleApprove}
                    disabled={actionState !== "none"}
                  >
                    {actionState === "approving" ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Check size={14} />
                    )}
                    Approve Order
                  </button>
                  <button
                    className="w-full py-2.5 rounded-lg text-xs font-semibold bg-white border border-red-200 text-red-600 hover:bg-red-50 transition-all cursor-pointer disabled:opacity-40 flex items-center justify-center gap-1.5"
                    onClick={() => setShowCancelModal(true)}
                  >
                    <AlertCircle size={14} />
                    Cancel Order
                  </button>
                </div>
              }
            />
          </div>
        </div>
      </div>
      <CancelOrderModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        orderId={order.orderId}
        orderNumber={order.orderNumber}
        onSuccess={onBack}
      />
    </div>
  );
};

export default ApproveOrder;
